/**
 * Debug Provider
 *
 * Wraps your app to enable component visualization debug mode.
 *
 * Usage:
 *   <DebugProvider>
 *     <App />
 *   </DebugProvider>
 */

import { useState, useEffect, useCallback } from "react";
import DebugContext from "./DebugContext";
import DebugOverlay from "./DebugOverlay";
import "./debug.scss";

const DebugProvider = ({ children }) => {
  // Check localStorage for persisted debug state
  const [isDebugMode, setIsDebugMode] = useState(() => {
    try {
      return localStorage.getItem("react-debug-mode") === "true";
    } catch {
      return false;
    }
  });

  // Depth level for filtering components (0 = outermost)
  const [depthLevel, setDepthLevel] = useState(0);
  
  // Max depth found in the component tree (updated by DebugOverlay)
  const [maxDepth, setMaxDepth] = useState(20);

  const toggleDebug = useCallback(() => {
    console.log("[Debug] Toggle clicked");
    setIsDebugMode((prev) => {
      const newValue = !prev;
      console.log("[Debug] Mode changed:", prev, "→", newValue);
      try {
        localStorage.setItem("react-debug-mode", String(newValue));
      } catch {
        // Ignore localStorage errors
      }
      return newValue;
    });
  }, []);

  // Keyboard shortcut: Ctrl+Shift+B or Cmd+Shift+B (B for Bug)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "B") {
        e.preventDefault();
        console.log("[Debug] Keyboard shortcut pressed");
        toggleDebug();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleDebug]);

  return (
    <DebugContext.Provider value={{ isDebugMode, toggleDebug, depthLevel, setDepthLevel, maxDepth, setMaxDepth }}>
      {children}
      {isDebugMode && <DebugOverlay />}
    </DebugContext.Provider>
  );
};

export default DebugProvider;
